import React, { memo, useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import AppStatusPill from "@/components/layout/AppStatusPill";
import { Calendar } from "@/components/ui/calendar";
import { useAppStatusTransitions } from "@/hooks/queries";
import { cn } from "@/lib/utils";
import { AppStatus, AppStatusChoice } from "@/types/borrower";
import { FilterOptionsSource } from "./filterRegistry";
import { FilterOption } from "./useFilterOptions";
import { Suggestion } from "./useSearchAutocomplete";

export const optionId = (listId: string, index: number) => `${listId}-option-${index}`;

type OptionView = (option: FilterOption, choices: AppStatusChoice[]) => React.ReactNode;

const OPTION_VIEWS: Partial<Record<FilterOptionsSource, OptionView>> = {
  status: (option, choices) => (
    <AppStatusPill status={option.value as AppStatus} choices={choices} />
  ),
  states: (option) => (
    <>
      <span className="w-9 shrink-0 font-semibold">{option.label}</span>
      {option.detail}
    </>
  ),
  brokers: (option) => (
    <span className="flex min-w-0 flex-col">
      <span className="break-words">{option.label}</span>
      {option.detail && (
        <span className="break-all text-xs text-muted-foreground">{option.detail}</span>
      )}
    </span>
  ),
};

const plainView: OptionView = (option) => <span className="break-all">{option.label}</span>;

/** How a value option of `source` is drawn, in the popover and the filters sheet alike. */
export const optionView = (source?: FilterOptionsSource): OptionView =>
  (source && OPTION_VIEWS[source]) || plainView;

interface SuggestionListProps {
  id: string;
  suggestions: Suggestion[];
  activeIndex: number;
  /** The value list's source; undefined for the key list. */
  source?: FilterOptionsSource;
  /** Adds the "Type to narrow" footer: the list is one page of more matches. */
  hasMore: boolean;
  coarse: boolean;
  onAccept: (suggestion: Suggestion) => void;
}

const SuggestionList = memo(function SuggestionList({
  id,
  suggestions,
  activeIndex,
  source,
  hasMore,
  coarse,
  onAccept,
}: SuggestionListProps) {
  const { choices } = useAppStatusTransitions({ enabled: source === "status" });
  const view = optionView(source);

  useEffect(() => {
    if (activeIndex >= 0) {
      document.getElementById(optionId(id, activeIndex))?.scrollIntoView({ block: "nearest" });
    }
  }, [id, activeIndex]);

  return (
    <ul
      id={id}
      role="listbox"
      className={cn(
        "overflow-y-auto overscroll-contain",
        // Desktop shows as much as fits below the bar (Radix measures it), then scrolls.
        coarse ? "max-h-80" : "max-h-[calc(var(--radix-popover-content-available-height)-1rem)]"
      )}
    >
      {suggestions.map((suggestion, index) => (
        <li
          key={
            suggestion.kind === "key"
              ? suggestion.filter.key
              : `${suggestion.option.value}|${suggestion.option.label}`
          }
          id={optionId(id, index)}
          role="option"
          aria-selected={index === activeIndex}
          // Keeps focus (and the mobile keyboard) on the input.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onAccept(suggestion)}
          className={cn(
            "flex cursor-pointer select-none items-center gap-2",
            coarse
              ? "min-h-11 border-b px-4 text-base last:border-b-0"
              : "rounded-sm px-3 py-1.5 text-sm [@media(hover:hover)]:hover:bg-accent/60",
            index === activeIndex && "bg-accent"
          )}
        >
          {suggestion.kind === "key" ? suggestion.filter.label : view(suggestion.option, choices)}
        </li>
      ))}
      {hasMore && (
        // A hint, not an option: hidden from the listbox's options and the arrow keys.
        <li
          aria-hidden
          onMouseDown={(event) => event.preventDefault()}
          className={cn(
            "flex cursor-default select-none items-center text-muted-foreground",
            coarse ? "min-h-11 px-4 text-base" : "px-3 py-1.5 text-sm"
          )}
        >
          Type to narrow
        </li>
      )}
    </ul>
  );
});

interface RangeCalendarProps {
  onPick: (range: { from: Date; to: Date }) => void;
}

/** The Workflow date filter's range calendar; a range is accepted once both ends are picked. */
export function RangeCalendar({ onPick }: RangeCalendarProps) {
  const [range, setRange] = useState<DateRange | undefined>();
  return (
    <Calendar
      mode="range"
      selected={range}
      numberOfMonths={1}
      onSelect={(next) => {
        setRange(next);
        if (next?.from && next.to) onPick({ from: next.from, to: next.to });
      }}
    />
  );
}

export default SuggestionList;
